// src/components/coordinator/GoalWidgets.js
//
// The Coordinator page's goal circles, leaderboard lists and goal-history
// pop-up. One implementation used once per metric (Enrollments, Screenings),
// so both circles and both leaderboards behave identically. Presentational
// only: CoordinatorPage owns the data and the writes; the rules for totals,
// percents and history are in services/goalProgress.js.

import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { motion, AnimatePresence } from "framer-motion";
import {
  Frown,
  Meh,
  Smile,
  Laugh,
  Pencil,
  Target,
  Stethoscope,
  History,
  X,
  CheckCircle2,
} from "lucide-react";
import { AnimatedNumber, modalBackdrop, modalPanel } from "../motionPresets";
import { UI_REVAMP_ENABLED } from "../../config/featureFlags";
import { periodLabel, PERIOD_TYPES } from "../../services/enrollmentPeriods";

ChartJS.register(ArcElement, Tooltip, Legend);

// Maps goal-progress % to a face icon + color. Sad below 25%, neutral around
// the halfway mark, happy past 50%, and a big grin as it nears the goal.
// The goal-face--* classes keep these colours in light mode (index.css).
export const getGoalFace = (pct) => {
  if (pct < 25) return { Icon: Frown, color: "text-rose-300 goal-face--sad", label: "sad" };
  if (pct < 50) return { Icon: Meh, color: "text-amber-300 goal-face--meh", label: "meh" };
  if (pct < 75) return { Icon: Smile, color: "text-emerald-300 goal-face--happy", label: "happy" };
  return { Icon: Laugh, color: "text-emerald-400 goal-face--grin", label: "big grin" };
};

// Per-metric look. Enrollments keep the original emerald; Screenings are sky.
export const METRIC_STYLE = {
  enrollments: {
    icon: Target,
    ring: "#34D399",
    ringBorder: "rgba(52,211,153,0.6)",
    text: "text-emerald-300",
  },
  screenings: {
    icon: Stethoscope,
    ring: "#38BDF8",
    ringBorder: "rgba(56,189,248,0.6)",
    text: "text-sky-300",
  },
};

// The unfilled part of the ring. Slate rather than translucent white so it
// still shows on the light-mode panel.
const RING_REST = "rgba(148,163,184,0.22)";
const RING_REST_BORDER = "rgba(148,163,184,0.3)";
const doughnutOptions = { cutout: "72%", plugins: { legend: { display: false }, tooltip: { enabled: false } } };

/** One monthly goal circle. `goal` null means "Goal not set". */
export const GoalCircle = ({ metric, label, total, goal, pct, canEditGoal, onEditGoal }) => {
  const style = METRIC_STYLE[metric];
  const hasGoal = goal != null && pct != null;
  const data = {
    labels: [label, "Remaining"],
    datasets: [
      {
        data: hasGoal ? [total, Math.max(goal - total, 0)] : [0, 1],
        backgroundColor: [style.ring, RING_REST],
        borderColor: [style.ringBorder, RING_REST_BORDER],
        borderWidth: 1,
      },
    ],
  };
  const face = hasGoal ? getGoalFace(pct) : null;

  return (
    <div className="min-w-0 flex flex-col items-center text-center">
      <div className="relative w-20 h-20 shrink-0">
        <Doughnut data={data} options={doughnutOptions} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {hasGoal ? (
            <>
              <face.Icon className={`w-6 h-6 ${face.color}`} strokeWidth={2} />
              <AnimatedNumber value={pct} suffix="%" className="mt-0.5 text-xs font-bold text-white leading-none" />
            </>
          ) : (
            <>
              {!UI_REVAMP_ENABLED && <style.icon className={`w-5 h-5 ${style.text}`} strokeWidth={2} />}
              <span className="mt-0.5 text-[9px] font-semibold text-slate-400 leading-none">No goal</span>
            </>
          )}
        </div>
      </div>
      <div className="mt-1.5 text-[10px] uppercase tracking-wider leading-tight text-slate-400 break-words">
        {/* zero-width space after the slash lets a long label wrap there */}
        {String(label).split("/").join("/​")}
      </div>
      {UI_REVAMP_ENABLED ? (
        <div className="text-base font-bold text-white leading-tight inline-flex items-center gap-1">
          <span>
            <AnimatedNumber value={total} className={style.text} />
            {hasGoal ? <span className="text-slate-500"> / {goal}</span> : null}
          </span>
          {/* Just the pencil, beside the numbers; the accessible name stays
              "Edit goal" / "Set goal". */}
          {canEditGoal && (
            <button
              type="button"
              onClick={onEditGoal}
              className="rx-icon-button"
              title={`Edit monthly ${label.toLowerCase()} goal`}
              aria-label={hasGoal ? "Edit goal" : "Set goal"}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
      ) : (
        <div className="text-base font-bold text-white leading-tight">
          <AnimatedNumber value={total} className={style.text} />
          {hasGoal ? <span className="text-slate-500"> / {goal}</span> : null}
        </div>
      )}
      {!hasGoal && <div className="text-[10px] text-slate-400">Goal not set</div>}
      {!UI_REVAMP_ENABLED && canEditGoal && (
        <button
          type="button"
          onClick={onEditGoal}
          className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[10px] font-semibold text-slate-300 transition"
          title={`Edit monthly ${label.toLowerCase()} goal`}
        >
          <Pencil className="w-2.5 h-2.5" />
          {hasGoal ? "Edit goal" : "Set goal"}
        </button>
      )}
    </div>
  );
};

/**
 * One ranked leaderboard (already ranked: count > 0, highest first).
 * `onEdit` is passed for admins only; without it there is no Edit button.
 */
export const LeaderboardList = ({ metric, title, ranked, emptyText, onEdit }) => {
  const style = METRIC_STYLE[metric];
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-1">
        {!UI_REVAMP_ENABLED && <style.icon className={`w-3 h-3 ${style.text} shrink-0`} />}
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-300 flex-1 truncate">
          {title}
        </span>
        {onEdit && UI_REVAMP_ENABLED && (
          <button
            type="button"
            onClick={onEdit}
            className="rx-icon-button"
            title={`Edit ${title.toLowerCase()} counts`}
            aria-label={`Edit ${title.toLowerCase()} counts`}
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
        {onEdit && !UI_REVAMP_ENABLED && (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-lg border border-white/10 bg-white/[0.04] px-2 text-[10px] font-semibold text-slate-300 transition hover:border-amber-300/30 hover:bg-amber-500/15 hover:text-amber-200"
            title={`Edit ${title.toLowerCase()} counts`}
          >
            <Pencil className="w-2.5 h-2.5 shrink-0" />
            Edit
          </button>
        )}
      </div>
      {ranked.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 bg-white/[0.02] px-3 py-3 text-center">
          <p className="text-[11px] text-slate-400">{emptyText}</p>
        </div>
      ) : (
        <ul className="space-y-0.5 max-h-[240px] overflow-y-auto pr-1">
          {ranked.map((c, i) => {
            const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
            return (
              <li
                key={c.name}
                className="flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-lg text-xs text-slate-200 hover:bg-white/[0.04] transition"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="w-5 text-center text-[10px] text-slate-400 font-mono">
                    {medal ?? `#${i + 1}`}
                  </span>
                  <span className="truncate">{c.name}</span>
                </div>
                <span className="font-mono font-semibold text-white tabular-nums shrink-0">{c.count}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const HistoryCell = ({ metric, value }) => {
  const style = METRIC_STYLE[metric];
  return (
    <div className="text-right">
      <div className="text-sm font-semibold text-white tabular-nums">
        <span className={style.text}>{value.total}</span>
        {value.goal != null && <span className="text-slate-500"> / {value.goal}</span>}
      </div>
      <div className="text-[10px] text-slate-400">
        {value.pct != null ? `${value.pct}% of goal` : "Goal not set"}
      </div>
    </div>
  );
};

/** Past months for both circles. Choosing a month shows it on the page. */
export const GoalHistoryModal = ({ open, loading, error, rows, selectedMonthId, onPick, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        {...modalBackdrop}
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <motion.div
          {...modalPanel}
          role="dialog"
          aria-modal="true"
          aria-label="Goal history"
          className="goal-history-modal relative w-full max-w-lg max-h-[85vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col"
        >
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-emerald-400 via-teal-400 to-sky-400 opacity-80 pointer-events-none" />
          <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
            <div className="goal-history-icon w-10 h-10 rounded-xl bg-emerald-500/15 border border-white/15 flex items-center justify-center">
              <History className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80 font-semibold leading-tight">
                Goal Progress
              </div>
              <h2 className="text-base font-semibold text-white leading-tight">Past months</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="goal-history-close w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="relative overflow-y-auto flex-1 p-4">
            {loading ? (
              <p className="text-sm text-slate-400 text-center py-6">Loading…</p>
            ) : error ? (
              <p className="text-sm text-rose-300 text-center py-6">{error}</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No months with counts yet.</p>
            ) : (
              <>
                <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 pb-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                  <span>Month</span>
                  <span className="w-24 text-right leading-tight">Randomizations</span>
                  <span className="w-24 text-right">Screenings</span>
                </div>
                <ul className="space-y-1">
                  {rows.map((r) => {
                    const selected = r.monthId === selectedMonthId;
                    return (
                      <li key={r.monthId}>
                        <button
                          type="button"
                          onClick={() => onPick(r.monthId)}
                          className={`goal-history-row w-full grid grid-cols-[1fr_auto_auto] gap-3 items-center px-3 py-2 rounded-xl border text-left transition ${
                            selected
                              ? "goal-history-row--selected border-emerald-400/40 bg-emerald-500/10"
                              : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                          }`}
                          title="Show this month on the page"
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-white truncate">
                              {periodLabel(PERIOD_TYPES.MONTH, r.monthId) || r.monthId}
                            </span>
                            {r.closed && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
                                <CheckCircle2 className="w-3 h-3" /> Closed
                              </span>
                            )}
                          </span>
                          <span className="w-24">
                            <HistoryCell metric="enrollments" value={r.enrollments} />
                          </span>
                          <span className="w-24">
                            <HistoryCell metric="screenings" value={r.screenings} />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-3 px-1 text-[11px] text-slate-400">
                  Totals are for coordinators on today's leaderboard, the same way the circles count them. Click a
                  month to view it on the page.
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
